import { useDepot } from '#~/depot/hooks.js';
import { getActiveStudioId } from '#~/analytics/active-studio.js';
import { useHotKeys } from '#~/hot-keys/hooks.js';
import { usePopupStore } from '#~/store/popup.js';
import type { Api } from '@studio/design-system/dialog';
import { useDialog } from '@studio/design-system/dialog';
import { ensureNotNil } from '@studio/js-utils';
import type { Organization } from '@studio/models/organization';
import type { RoomRecord } from '@studio/models/room';
import { normalizeId } from '@studio/models/identifier';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { createElement, forwardRef } from 'react';
import { shallow } from 'zustand/shallow';
import type { Props } from './settings/index.js';
import { Panel, Settings, TopLevelPanel } from './settings/index.js';

// @internal
const SettingsWrapper = forwardRef<
  Api,
  Omit<Props, 'account' | 'hotKeys' | 'organizations' | 'rooms' | 'user'>
>((props, forwardedRef): ReactElement => {
  const hotKeys = useHotKeys();

  const [account, user, organizations, rooms] = useDepot(
    (µ) => [ensureNotNil(µ.account), ensureNotNil(µ.user), µ.organizations, µ.roomRecords],
    shallow
  );

  return createElement(Settings, {
    account,
    hotKeys,
    user,
    organizations,
    rooms,
    ...props,
    ref: forwardedRef
  });
});

/**
 * Hook that returns a function to imperatively open the account settings.
 *
 * @returns Imperative function.
 */
export const useSettings = (): ((
  defaultPanel?: Panel,
  id?: string,
  options?: { readonly openPlanPicker?: boolean }
) => void) => {
  const dialog = useDialog(SettingsWrapper);
  const defaultTopLevelPanel = TopLevelPanel.Account;
  const { hidePopup } = usePopupStore();

  return useLatestFn(
    (defaultPanel?: Panel, id?: string, options?: { readonly openPlanPicker?: boolean }) => {
      let overrideOrganizationId: T_Maybe<Organization['id']>;
      let overrideRoomId: T_Maybe<RoomRecord['id']>;
      let initialPlanPickerRoomId: RoomRecord['id'] | undefined;

      if (options?.openPlanPicker) {
        initialPlanPickerRoomId = id === undefined ? undefined : normalizeId<RoomRecord['id']>(id);
        // An expiry action cannot open billing for a Studio selected since display.
        if (
          defaultPanel !== Panel.Billing ||
          initialPlanPickerRoomId === undefined ||
          initialPlanPickerRoomId !== getActiveStudioId()
        )
          return;
      }

      if (defaultPanel === Panel.Studio) {
        overrideRoomId = <T_Maybe<RoomRecord['id']>>id;
      }

      if (defaultPanel === Panel.Organization) {
        overrideOrganizationId = <T_Maybe<Organization['id']>>id;
      }

      hidePopup();
      dialog.open({
        defaultPanel,
        defaultTopLevelPanel,
        overrideOrganizationId,
        overrideRoomId,
        initialPlanPickerRoomId
      });
    }
  );
};
